<!-- src/components/Admin/ImpersonationModal.vue -->
<template>
  <div
    class="fixed inset-0 z-[9997] flex items-start justify-center bg-black/60 p-4 pt-20"
    @click.self="closeModal"
  >
    <div class="w-full max-w-md transform rounded-xl bg-white shadow-2xl transition-all">
      <div class="border-b border-gray-200 p-5">
        <h2 class="text-lg font-semibold text-gray-800">Impersonate User</h2>
        <p class="mt-1 text-sm text-gray-500">Select a user to view the application as them.</p>
      </div>
      <div class="p-5">
        <input
          type="text"
          v-model="searchTerm"
          placeholder="Search by name or email..."
          class="form-input w-full"
        />

        <div class="mt-4 max-h-80 overflow-y-auto rounded-md border border-gray-200">
          <ul class="divide-y divide-gray-200">
            <li
              v-for="user in filteredUsers"
              :key="user.id"
              @click="handleImpersonate(user)"
              class="flex cursor-pointer items-center justify-between p-3 transition-colors hover:bg-indigo-50"
            >
              <div>
                <p class="text-sm font-medium text-gray-900">{{ user.displayName }}</p>
                <p class="text-xs text-gray-500">{{ user.email }}</p>
              </div>
              <span
                class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold capitalize text-gray-700"
              >
                {{ user.roles?.userRole }}
              </span>
            </li>
            <li v-if="!filteredUsers.length" class="p-4 text-center text-sm text-gray-500">
              No users match your search.
            </li>
          </ul>
        </div>
      </div>
      <div class="rounded-b-xl bg-gray-50 px-5 py-3 text-right">
        <button @click="closeModal" class="btn-secondary">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { getAuth, signInWithCustomToken } from 'firebase/auth'
import { useToast } from '@/composables/useToast'
import { useUserStore } from '@/stores/userStore'
import { useUsersStore } from '@/stores/usersStore'
import { eventBus } from '@/utils/eventBus'

const emit = defineEmits(['close'])
const toast = useToast()
const userStore = useUserStore()
const usersStore = useUsersStore()

const searchTerm = ref('')

const allUsers = computed(() => {
  // The usersStore.selectableUsers is an object with UIDs as keys. Convert to array.
  // Resolve V2 nested identity fields with V1 flat field fallback.
  return (
    Object.entries(usersStore.selectableUsers)
      .map(([id, data]) => {
        const displayName =
          data.identity?.displayName ||
          data.displayName ||
          [data.identity?.firstName, data.identity?.lastName].filter(Boolean).join(' ') ||
          [data.firstName, data.lastName].filter(Boolean).join(' ') ||
          ''
        const email = data.identity?.email || data.email || ''
        return { id, ...data, displayName, email }
      })
      // Filter out the current super admin so they can't impersonate themselves
      .filter(u => u.email !== userStore.user.email)
  )
})

const filteredUsers = computed(() => {
  if (!searchTerm.value) {
    return allUsers.value
  }
  const lowerSearch = searchTerm.value.toLowerCase()
  return allUsers.value.filter(
    user =>
      user.displayName?.toLowerCase().includes(lowerSearch) ||
      user.email?.toLowerCase().includes(lowerSearch)
  )
})

const handleImpersonate = async targetUser => {
  eventBus.emit('confirm', {
    title: 'Confirm Impersonation',
    text: `Are you sure you want to view the system as ${targetUser.displayName}? You will be logged in as them until you exit impersonation mode.`,
    confirmButtonText: 'Yes, Impersonate',
    onConfirm: async () => {
      eventBus.emit('loading-start')
      try {
        const functions = getFunctions()
        const impersonateUser = httpsCallable(functions, 'impersonateUser')
        const result = await impersonateUser({ targetUid: targetUser.id })

        if (result.data.success) {
          // THE FIX: Set a flag before signing in and reloading.
          sessionStorage.setItem('isImpersonationReload', 'true')

          const auth = getAuth()
          await signInWithCustomToken(auth, result.data.token)

          toast.success(`Now viewing as ${targetUser.displayName}.`)

          // Full page reload is the most reliable way to re-initialize all stores and state
          window.location.reload()
        } else {
          throw new Error(result.data.error || 'Failed to get impersonation token.')
        }
      } catch (error) {
        console.error('Impersonation error:', error)
        toast.error(error.message || 'An error occurred during impersonation.')
        eventBus.emit('loading-stop')
      }
    },
  })
}

const closeModal = () => {
  emit('close')
}
</script>
